import React from "react";
import {
  VictoryChart,
  VictoryAxis,
  VictoryBoxPlot,
  VictoryLabel,
} from "victory";
import * as Colors from "../Colors";
import { sortData } from "./ChartHelper";
import { ResponsiveContainer, Text } from "recharts";

import theme from "./BoxPlotTheme";

function formatLabel(value) {
  if (value >= 1.0e3) {
    return (Math.abs(Number(value)) / 1.0e3).toFixed(1) + "K";
  }
  return value > 100 ? value.toFixed(0) : value.toFixed(1);
}

function formatSpacing(num) {
  if (num === 2) {
    return { x: [400, 400], y: 30 };
  }
  return 30;
}

function CustomXAxisLabel(props) {
  const { x, y, text, style, startLabel, endLabel } = props;
  const mainLabelOffset = startLabel ? 20 : 0;
  const endOffset = 65;
  const middleAnchorOffset = 10;

  return (
    <g>
      <Text
        x={x}
        y={y + mainLabelOffset}
        dy={middleAnchorOffset}
        textAnchor="middle"
        verticalAnchor="middle"
        fill={Colors.PURPLE}
        fontSize={style.fontSize}
      >
        {text}
      </Text>
      {startLabel && (
        <Text
          x={endOffset}
          y={y}
          dy={middleAnchorOffset}
          textAnchor="start"
          verticalAnchor="middle"
          fill={Colors.PURPLE}
          fontSize="1.3rem"
        >
          {startLabel}
        </Text>
      )}
      {startLabel && endLabel && (
        <Text
          x={x * 2 - endOffset}
          y={y}
          dy={middleAnchorOffset}
          textAnchor="end"
          verticalAnchor="middle"
          fill={Colors.PURPLE}
          fontSize="1.3rem"
        >
          {endLabel}
        </Text>
      )}
      {endLabel && (
        <Text
          x={x}
          y={y}
          dy={middleAnchorOffset}
          textAnchor="middle"
          verticalAnchor="middle"
          fill={Colors.PURPLE}
          fontSize="2.4rem"
        >
          ⟶
        </Text>
      )}
    </g>
  );
}

function fixAverageData(data) {
  const avgColumnIndex = data.findIndex(
    (d) => d.name === "Average" || d.name === "Cumulative"
  );
  if (avgColumnIndex !== -1) {
    data[avgColumnIndex].value = data
      .filter((d, index) => index !== avgColumnIndex)
      .reduce((arr, d) => arr.concat(d.value), []);
  }
  return data;
}

export default function BoxPlot({ data, options }) {
  data = data ?? [];
  data = sortData(data, options);
  data = fixAverageData(data);

  const dx = options.dx ?? 0;
  const dy = options.dy ?? 10;
  const yAxisPadding = options.yAxisPadding ?? 65;
  const yLabelPadding = options.yLabelPadding ?? 40;
  const xAxisPadding = options.xAxisPadding ?? options.xLabelStart ? 80 : 60;
  const xLabelPadding = options.xLabelPadding ?? 30;
  const boxWidth = Object.keys(data).length === 2 ? 35 : 20;
  const domain = options.yMax ? { y: [0, options.yMax] } : undefined;

  function renderBoxPlot(options) {
    if (options.showMinMax === false) {
      return (
        <VictoryBoxPlot
          boxWidth={boxWidth}
          medianLabels={({ datum }) => formatLabel(datum._median)}
          medianLabelComponent={
            <VictoryLabel dy={0} dx={boxWidth / 2 + 5} textAnchor="start" />
          }
          data={data}
          x="name"
          y="value"
        />
      );
    } else {
      return (
        <VictoryBoxPlot
          boxWidth={boxWidth}
          maxLabels={({ datum }) => formatLabel(datum._max)}
          maxLabelComponent={
            <VictoryLabel dy={-10} dx={0} textAnchor="middle" />
          }
          minLabels={({ datum }) => formatLabel(datum._min)}
          minLabelComponent={
            <VictoryLabel dy={dy} dx={dx} textAnchor="middle" />
          }
          medianLabels={({ datum }) => formatLabel(datum._median)}
          medianLabelComponent={
            <VictoryLabel dy={0} dx={boxWidth / 2 + 5} textAnchor="start" />
          }
          data={data}
          x="name"
          y="value"
        />
      );
    }
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <VictoryChart
        domainPadding={formatSpacing(Object.keys(data).length)}
        theme={theme}
        padding={{
          top: 20,
          bottom: xAxisPadding,
          right: 30,
          left: yAxisPadding,
        }}
        domain={domain}
      >
        <VictoryAxis
          label={options.yLabel}
          dependentAxis
          style={{
            axisLabel: { fontSize: 14, padding: yLabelPadding },
          }}
        />
        {renderBoxPlot(options)}
        <VictoryAxis
          label={options.xLabel}
          style={{
            axisLabel: { fontSize: 14, padding: xLabelPadding },
          }}
          fixLabelOverlap={true}
          axisLabelComponent={
            <CustomXAxisLabel
              startLabel={options.xLabelStart}
              endLabel={options.xLabelEnd}
            />
          }
        />
      </VictoryChart>
    </ResponsiveContainer>
  );
}
